import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import { Button, Card, ErrorNote, Input, Label } from '../components/ui.js';

export function ForgotPasswordPage({ panelName }: { panelName: string }) {
  const [email, setEmail] = useState('');
  const request = useMutation({
    mutationFn: () => api.post('/api/auth/password-reset/request', { email }),
  });
  return (
    <div className="flex h-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <Card className="w-full max-w-sm space-y-4 p-6">
        <h1 className="text-lg font-semibold">{panelName}</h1>
        <h2 className="font-medium">Forgot your password?</h2>
        {request.isSuccess ? (
          <p className="text-sm">If this email belongs to an active account and recovery email is available, a reset link is on its way. If it does not arrive, contact your hosting provider.</p>
        ) : (
          <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); request.mutate(); }}>
            <div><Label htmlFor="recovery-email">Login email</Label>
              <Input id="recovery-email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
            </div>
            {request.error && <ErrorNote error={request.error} />}
            <Button type="submit" className="w-full" loading={request.isPending}>Send reset link</Button>
          </form>
        )}
        <Link to="/" className="block text-center text-sm text-brand-600">Back to sign in</Link>
      </Card>
    </div>
  );
}

export function ResetPasswordPage({ panelName }: { panelName: string }) {
  const [token] = useState(() => {
    const value = window.location.hash.slice(1);
    window.history.replaceState(null, '', window.location.pathname);
    return value;
  });
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const complete = useMutation({
    mutationFn: () => api.post('/api/auth/password-reset/complete', { token, newPassword: password }),
    onSuccess: () => {
      queryClient.clear();
    },
  });
  return (
    <div className="flex h-full items-center justify-center bg-slate-50 p-6 dark:bg-slate-950">
      <Card className="w-full max-w-sm space-y-4 p-6">
        <h1 className="text-lg font-semibold">{panelName}</h1>
        <h2 className="font-medium">Set a new password</h2>
        {!token ? <p className="text-sm">This password link is missing or invalid. Request a new link.</p>
          : complete.isSuccess ? <>
            <p className="text-sm">Password updated. Sign in with your new password and your usual two-factor code, if enabled.</p>
            <Button onClick={() => navigate('/')}>Sign in</Button>
          </> : (
            <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); if (password === confirmation) complete.mutate(); }}>
              <div><Label htmlFor="new-recovery-password">New password</Label>
                <Input id="new-recovery-password" type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(event) => setPassword(event.target.value)} />
              </div>
              <div><Label htmlFor="confirm-recovery-password">Confirm password</Label>
                <Input id="confirm-recovery-password" type="password" autoComplete="new-password" minLength={12} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
              </div>
              {password && confirmation && password !== confirmation && <p className="text-sm text-red-600">Passwords do not match.</p>}
              {complete.error && <ErrorNote error={complete.error} />}
              <Button type="submit" className="w-full" disabled={password !== confirmation} loading={complete.isPending}>Set password</Button>
            </form>
          )}
        <Link to="/forgot-password" className="block text-center text-sm text-brand-600">Request another link</Link>
      </Card>
    </div>
  );
}
